import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchMeters,
  fetchMeterHistory,
  fetchWells,
  fetchMeterTypes,
  fetchUnitNatives,
  fetchGalConversions,
  createMeter,
  updateMeter,
  toggleRetiredMeter,
  installMeter,
  removeMeter,
  transferMeter,
} from "./services";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../AppProvider";
import { MESSAGES, QUERY_OPTIONS_CACHE } from "./constants";

export function useMetersManagement() {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();
  const queryClient = useQueryClient();

  // Queries
  const metersQuery = useQuery(
    ["meters"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchMeters(token);
    },
    QUERY_OPTIONS_CACHE
  );

  const wellsQuery = useQuery(
    ["wells"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchWells(token);
    },
    QUERY_OPTIONS_CACHE
  );

  const meterTypesQuery = useQuery(
    ["meter-types"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchMeterTypes(token);
    },
    QUERY_OPTIONS_CACHE
  );

  const unitNativesQuery = useQuery(
    ["unit-natives"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchUnitNatives(token);
    },
    QUERY_OPTIONS_CACHE
  );

  const galConversionsQuery = useQuery(
    ["gal-conversions"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchGalConversions(token);
    },
    QUERY_OPTIONS_CACHE
  );

  const useMeterHistoryQuery = (meterId) =>
    useQuery(
      ["meter-history", meterId],
      async () => {
        const token = await getAccessTokenSilently();
        return fetchMeterHistory(token, meterId);
      },
      {
        enabled: !!meterId,
      }
    );

  // Mutations
  const createOrUpdateMutation = useMutation(
    async ({ formData, isCreateMode }) => {
      const token = await getAccessTokenSilently();
      if (isCreateMode) {
        return createMeter({ token, payload: formData });
      } else {
        return updateMeter({
          token,
          meterId: formData.meter_ndx,
          payload: formData,
        });
      }
    },
    {
      onSuccess: (data, { isCreateMode }) => {
        void queryClient.invalidateQueries(["meters"]);
        doToast(
          "success",
          data?.message ||
            (isCreateMode ? MESSAGES.SUCCESS_CREATE : MESSAGES.SUCCESS_UPDATE)
        );
      },
      onError: (error) => {
        console.error("Create/Update error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_CREATE;
        doToast("error", `Error: ${msg}`);
      },
    }
  );

  const retireMutation = useMutation(
    async (meterId) => {
      const token = await getAccessTokenSilently();
      return toggleRetiredMeter({ token, meterId });
    },
    {
      onSuccess: (data) => {
        void queryClient.invalidateQueries(["meters"]);
        doToast(
          "success",
          data?.message || "Meter status updated successfully"
        );
      },
      onError: (error) => {
        console.error("Retire error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          "Failed to update meter status";
        doToast("error", `Error: ${msg}`);
      },
    }
  );

  const installMutation = useMutation(
    async ({ meterId, payload }) => {
      const token = await getAccessTokenSilently();
      return installMeter({ token, meterId, payload });
    },
    {
      onSuccess: (data) => {
        void queryClient.invalidateQueries(["meters"]);
        void queryClient.invalidateQueries(["meter-history"]);
        void queryClient.invalidateQueries(["wells"]);
        doToast("success", data?.message || MESSAGES.SUCCESS_INSTALL);
      },
      onError: (error) => {
        console.error("Install error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_INSTALL;
        doToast("error", `Error: ${msg}`);
      },
    }
  );

  const removeMutation = useMutation(
    async ({ meterId, payload }) => {
      const token = await getAccessTokenSilently();
      return removeMeter({ token, meterId, payload });
    },
    {
      onSuccess: (data) => {
        void queryClient.invalidateQueries(["meters"]);
        void queryClient.invalidateQueries(["meter-history"]);
        void queryClient.invalidateQueries(["wells"]);
        doToast("success", data?.message || MESSAGES.SUCCESS_REMOVE);
      },
      onError: (error) => {
        console.error("Remove error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_REMOVE;
        doToast("error", `Error: ${msg}`);
      },
    }
  );

  const transferMutation = useMutation(
    async ({ meterId, payload }) => {
      const token = await getAccessTokenSilently();
      return transferMeter({ token, meterId, payload });
    },
    {
      onSuccess: (data) => {
        void queryClient.invalidateQueries(["meters"]);
        void queryClient.invalidateQueries(["meter-history"]);
        void queryClient.invalidateQueries(["wells"]);
        doToast("success", data?.message || MESSAGES.SUCCESS_TRANSFER);
      },
      onError: (error) => {
        console.error("Transfer error:", error);
        const msg =
          error?.response?.data?.error ||
          error.message ||
          MESSAGES.ERROR_TRANSFER;
        doToast("error", `Error: ${msg}`);
      },
    }
  );

  return {
    // Queries
    metersQuery,
    wellsQuery,
    meterTypesQuery,
    unitNativesQuery,
    galConversionsQuery,
    useMeterHistoryQuery,
    // Mutations
    createOrUpdateMutation,
    retireMutation,
    installMutation,
    removeMutation,
    transferMutation,
  };
}
